import { useAuth } from '@lastbrand/fe-common/universal-auth'
import { GetServerSidePropsContext, GetServerSidePropsResult, InferGetServerSidePropsType } from 'next'
import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong'
import {
  REDIRECT_FLAG,
  useRedirectToCheckoutIfApplicable,
} from '@src/quince-site-integrated/src/templates/Auth/hooks/use-login-redirect'
import LogIn from '@src/quince-site-integrated/src/templates/Auth/LogIn'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'
import { isB2BSelfServeDomain } from '@src/utils/b2b/b2b.utils'

import Layout from '../layout'

type LoginPageProps = ServerSideBaseProps & { isB2BLoginPage?: boolean }

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<LoginPageProps>> => {
  const serverSideBaseProps = await getServerSideBaseProps(ctx, PAGE_PATH.LOG_IN)
  const host = ctx.req.headers.host
  if (isB2BSelfServeDomain(host)) {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    return {
      ...serverSideBaseProps,
      props: {
        ...serverSideBaseProps.props,
        isB2BLoginPage: true,
      },
    }
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
  }
  return serverSideBaseProps
}

export default function PageLogIn(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const searchParams = useSearchParams()
  const search = searchParams.toString()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { isLoggedIn, isInitializing } = useAuth()
  const redirectToCheckoutIfApplicable = useRedirectToCheckoutIfApplicable()

  useEffect(() => {
    // Clear stale redirect flag from a previous OAuth cycle in this tab.
    // Only clear when this is NOT an OAuth callback (no ?code= param), since
    // the flag is needed during the callback page load to prevent redirect races.
    const hasOAuthCode = new URLSearchParams(window.location.search).has('code') // eslint-disable-line i18next/no-literal-string
    if (!hasOAuthCode) {
      // eslint-disable-next-line @quince/no-direct-sessionstorage
      sessionStorage.removeItem(REDIRECT_FLAG)
    }
  }, [])

  useEffect(() => {
    // Wait for auth to initialize before deciding to redirect
    if (isInitializing) return

    if (isLoggedIn) {
      // Already logged in: check if returnUrl is a B2B URL and handle cookie transfer + redirect.
      // useRedirectToCheckoutIfApplicable reads returnUrl from the query string, sets cross-domain
      // cookies if needed, and redirects. This covers the case where the user lands on /log-in
      // from B2B header click but is already authenticated on D2C.
      redirectToCheckoutIfApplicable()
      return
    }
  }, [isInitializing, isLoggedIn, redirectToCheckoutIfApplicable])

  return (
    <Layout {...props}>
      {props.pageData?.authenticationPage ? (
        <LogIn data={props.pageData} location={{ search }} />
      ) : (
        <SomethingWentWrong />
      )}
    </Layout>
  )
}
